import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, Panel } from "@/components/shared/PageUi";
import { toast } from "sonner";
import { Save, Loader2, Building2, Shield, Banknote, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";

const defaults = {
  company_name: "",
  business_id: "",
  company_address: "",
  tyel_company: "",
  tyel_contract_number: "",
  accident_insurance_company: "",
  accident_insurance_number: "",
  default_tyel_percent: null,
  default_unemployment_percent: 1.50,
  default_health_insurance_percent: 1.36,
  default_pay_day_of_month: 15,
  default_payment_method: "bank_transfer",
  bookkeeping_account_wages: "5000",
  bookkeeping_account_tyel: "5100",
  bookkeeping_account_taxes_payable: "2960",
};

export default function PayrollSettings() {
  const [data, setData] = useState(defaults);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/payroll/settings");
        setData({ ...defaults, ...(r.data || {}) });
      } catch (e) { toast.error(formatApiError(e)); }
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/payroll/settings", data);
      toast.success("Asetukset tallennettu");
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  const upd = (k, v) => setData({ ...data, [k]: v });

  if (loading) return <div className="text-center py-12 text-white/40">Ladataan…</div>;

  return (
    <div className="space-y-4" data-testid="payroll-settings">
      <PageHeader
        kicker="Palkat"
        title="Palkanmaksun asetukset"
        description="Yritys, vakuutukset, maksutavat ja kirjanpidon tilit."
        actions={
          <div className="flex gap-2">
            <Link to="/hr"><Button variant="ghost">Takaisin</Button></Link>
            <Button onClick={save} disabled={saving} className="btn-luxe inline-flex items-center gap-2" data-testid="settings-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Tallenna
            </Button>
          </div>
        }
      />

      <Panel title={<span className="inline-flex items-center gap-2"><Building2 className="w-4 h-4" /> Yritystiedot (työnantaja)</span>}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Yrityksen nimi"><Input value={data.company_name} onChange={e => upd("company_name", e.target.value)} data-testid="set-company" /></Field>
          <Field label="Y-tunnus"><Input value={data.business_id} onChange={e => upd("business_id", e.target.value)} placeholder="1234567-8" data-testid="set-bid" /></Field>
          <Field label="Osoite"><Input value={data.company_address} onChange={e => upd("company_address", e.target.value)} data-testid="set-address" /></Field>
        </div>
      </Panel>

      <Panel title={<span className="inline-flex items-center gap-2"><Shield className="w-4 h-4" /> Vakuutukset</span>}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="TyEL-yhtiö"><Input value={data.tyel_company} onChange={e => upd("tyel_company", e.target.value)} placeholder="Veritas / Ilmarinen / Varma…" /></Field>
          <Field label="TyEL-sopimusnumero"><Input value={data.tyel_contract_number} onChange={e => upd("tyel_contract_number", e.target.value)} /></Field>
          <Field label="Tapaturmavakuutus-yhtiö"><Input value={data.accident_insurance_company} onChange={e => upd("accident_insurance_company", e.target.value)} placeholder="If / LähiTapiola / Pohjola…" /></Field>
          <Field label="Tapaturmavakuutuksen sopimusnumero"><Input value={data.accident_insurance_number} onChange={e => upd("accident_insurance_number", e.target.value)} /></Field>
        </div>
      </Panel>

      <Panel title={<span className="inline-flex items-center gap-2"><Banknote className="w-4 h-4" /> Vähennysten oletusprosentit (2026)</span>}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="TyEL %, oma yliajo (jätä tyhjäksi = autoikä)">
            <Input
              type="number" step="0.01" value={data.default_tyel_percent ?? ""}
              placeholder="auto: 7.15 tai 8.65"
              onChange={e => upd("default_tyel_percent", e.target.value === "" ? null : parseFloat(e.target.value))}
            />
          </Field>
          <Field label="Työttömyysvakuutusmaksu %"><Input type="number" step="0.01" value={data.default_unemployment_percent} onChange={e => upd("default_unemployment_percent", parseFloat(e.target.value) || 0)} /></Field>
          <Field label="Sairausvakuutus päivärahamaksu %"><Input type="number" step="0.01" value={data.default_health_insurance_percent} onChange={e => upd("default_health_insurance_percent", parseFloat(e.target.value) || 0)} /></Field>
          <Field label="Maksupäivän oletus (kk:n päivä)"><Input type="number" min="1" max="31" value={data.default_pay_day_of_month} onChange={e => upd("default_pay_day_of_month", parseInt(e.target.value) || 15)} /></Field>
          <Field label="Maksutapa oletuksena">
            <select value={data.default_payment_method} onChange={e => upd("default_payment_method", e.target.value)} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm">
              <option value="bank_transfer">Tilisiirto viitenumerolla</option>
              <option value="paytrail">Paytrail-verkkomaksu</option>
              <option value="manual">Manuaalinen</option>
            </select>
          </Field>
        </div>
        <p className="text-[11px] text-white/50 mt-3">
          TyEL-prosentti määräytyy työntekijän iän mukaan: 53–62-vuotiaat 8.65 %, muut 7.15 %.
          Voit halutessasi pakottaa kiinteän prosentin yliajamalla.
        </p>
      </Panel>

      <Panel title={<span className="inline-flex items-center gap-2"><BookOpen className="w-4 h-4" /> Kirjanpidon tilit</span>}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Palkkakulu (Debit)"><Input value={data.bookkeeping_account_wages} onChange={e => upd("bookkeeping_account_wages", e.target.value)} placeholder="5000" /></Field>
          <Field label="TyEL-maksuvelka"><Input value={data.bookkeeping_account_tyel} onChange={e => upd("bookkeeping_account_tyel", e.target.value)} placeholder="5100" /></Field>
          <Field label="Ennakonpidätysvelka"><Input value={data.bookkeeping_account_taxes_payable} onChange={e => upd("bookkeeping_account_taxes_payable", e.target.value)} placeholder="2960" /></Field>
        </div>
        <p className="text-[11px] text-white/50 mt-3">
          Maksetut palkkalaskelmat kirjautuvat automaattisesti kirjanpitoon (Kirjanpito-moduuliin) palkkakuluna ja vastaavina velkoina.
        </p>
      </Panel>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-white/60">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
